import type { OnInit } from '@angular/core';
import type { WebWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { ELineStyle } from '@common/enums/line-style.enum';

import {
    lineColorCss,
    lineMarginLeftCss,
    lineMarginRightCss,
    lineSizeCss,
    lineStyleCss,
    lineThicknessCss,
    lineWrapperMarginCss,
} from '@sites/entity-core/src/widget/line/styles/line.css-vars';
import type { LineForm } from '@sites/entity-signal-form';

import { WebWidgetSignalFormService } from '@web/core/services/web-widget-signal-form.service';
import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import { colorCss } from '@web/widgets/common/css-vars/css-vars.utils';

/**
 * Editor preview for the `line` (divider) widget. The form is shared with the
 * control panel via `WebWidgetSignalFormService` (ref-counted by widget id), and
 * its accessors read each `props` leaf's DESKTOP value — like the published
 * renderer, the divider draws no per-device settings yet.
 */
@Component({
    selector: 'web-line-view',
    templateUrl: './line-view.component.html',
    styleUrls: ['./line-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineViewComponent extends WebBaseViewSignalFormComponent<WebWidgetEntity> implements OnInit {
    private readonly signalFormService: WebWidgetSignalFormService = inject(WebWidgetSignalFormService);

    public form!: LineForm;

    /**
     * Safari draws `border-top-style: dotted` as squares, so the dotted variant
     * falls back to a row of ● glyphs. Detected the same way the legacy view base
     * did, once per instance instead of on every widget emission.
     */
    public readonly isSafari: boolean = (() => {
        const ua = navigator.userAgent;
        return ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
    })();

    // --- CSS custom property values (bound on the wrapper, inherited by the divider) ---
    public readonly wrapperMargin = computed<string | null>(() => lineWrapperMarginCss(this.form.margin().value()));
    public readonly lineColor = computed<string | null>(() =>
        lineColorCss(this.form.borderTopColor().value(), colorCss)
    );
    public readonly lineStyle = computed<string>(() => lineStyleCss(this.form.borderTopStyle().value()));
    public readonly lineThickness = computed<string>(() => lineThicknessCss(this.form.borderTopWidth().value()));
    public readonly lineSize = computed<string>(() => lineSizeCss(this.form.width().value()));
    public readonly marginLeft = computed<string>(() => lineMarginLeftCss(this.form.horizontalAlign().value()));
    public readonly marginRight = computed<string>(() => lineMarginRightCss(this.form.horizontalAlign().value()));

    public readonly isDottedSafari = computed<boolean>(
        () => this.isSafari && this.form.borderTopStyle().value() === ELineStyle.DOTTED
    );

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<LineForm>(this.widget().id, this.destroyRef);
    }
}
